import React, { useState, useEffect } from 'react';

const CampaignDashboard = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: '2025-08-01',
    to: '2025-08-03'
  });

  // Theme colors
  const theme = {
    primary: '#2563eb',
    success: '#16a34a',
    danger: '#dc2626',
    warning: '#ca8a04',
    gray: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827'
    }
  };

  // Styles
  const styles = {
    // Main Layout
    pageContainer: {
      minHeight: '100vh',
      backgroundColor: theme.gray[50],
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
      direction: 'rtl'
    },
    
    // Header Section
    header: {
      backgroundColor: 'white',
      borderBottom: `1px solid ${theme.gray[200]}`,
      padding: '24px 0',
      marginBottom: '32px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
    },
    
    headerContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 24px'
    },
    
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      flexWrap: 'wrap',
      gap: '16px'
    },
    
    title: {
      fontSize: '24px',
      fontWeight: '700',
      color: theme.gray[900],
      margin: 0
    },
    
    // Summary Cards
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '24px'
    },
    
    summaryCard: {
      backgroundColor: 'white',
      padding: '16px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      border: `1px solid ${theme.gray[200]}`
    },
    
    summaryIcon: {
      width: '40px',
      height: '40px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      fontSize: '20px'
    },
    
    summaryContent: {
      flex: 1
    },
    
    summaryLabel: {
      fontSize: '12px',
      color: theme.gray[600],
      marginBottom: '2px'
    },
    
    summaryValue: {
      fontSize: '18px',
      fontWeight: '700',
      color: theme.gray[900]
    },
    
    // Date Controls
    dateSection: {
      backgroundColor: 'white',
      padding: '16px',
      borderRadius: '10px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      border: `1px solid ${theme.gray[200]}`
    },
    
    dateControls: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    
    dateInput: {
      padding: '8px 12px',
      border: `1px solid ${theme.gray[300]}`,
      borderRadius: '6px',
      fontSize: '14px',
      color: theme.gray[700],
      backgroundColor: 'white',
      outline: 'none',
      transition: 'border-color 0.2s',
      cursor: 'pointer'
    },
    
    updateButton: {
      padding: '8px 20px',
      backgroundColor: theme.primary,
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      transition: 'all 0.2s'
    },
    
    // Campaign Cards
    campaignGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '20px',
      marginBottom: '24px'
    },
    
    campaignCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      border: `1px solid ${theme.gray[200]}`,
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    
    campaignHeader: {
      padding: '16px',
      backgroundColor: theme.gray[50],
      borderBottom: `1px solid ${theme.gray[200]}`
    },
    
    campaignTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: theme.gray[900],
      marginBottom: '6px'
    },
    
    campaignMeta: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '8px'
    },
    
    campaignId: {
      fontSize: '12px',
      color: theme.gray[500]
    },
    
    campaignBudget: {
      fontSize: '12px',
      color: theme.primary,
      fontWeight: '600'
    },
    
    campaignBody: {
      padding: '16px'
    },
    
    // Stats Grid in Campaign Card
    statsRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '12px',
      marginBottom: '16px'
    },
    
    statItem: {
      textAlign: 'center',
      padding: '12px 6px',
      backgroundColor: theme.gray[50],
      borderRadius: '6px'
    },
    
    statValue: {
      fontSize: '20px',
      fontWeight: '700',
      marginBottom: '2px'
    },
    
    statLabel: {
      fontSize: '11px',
      color: theme.gray[600],
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    
    // Profit Section
    profitSection: {
      padding: '12px',
      borderRadius: '6px',
      textAlign: 'center'
    },
    
    profitLabel: {
      fontSize: '12px',
      color: theme.gray[600],
      marginBottom: '6px'
    },
    
    profitValue: {
      fontSize: '22px',
      fontWeight: '700'
    },
    
    // Modal Styles
    modal: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      zIndex: 1000,
      animation: 'fadeIn 0.2s ease-out'
    },
    
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '20px',
      maxWidth: '1200px',
      width: '100%',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      animation: 'slideUp 0.3s ease-out'
    },
    
    modalHeader: {
      padding: '24px 32px',
      borderBottom: `1px solid ${theme.gray[200]}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    
    modalTitle: {
      fontSize: '20px',
      fontWeight: '700',
      color: theme.gray[900]
    },
    
    modalBody: {
      padding: '32px',
      overflowY: 'auto',
      flex: 1
    },
    
    // Day Cards in Modal
    daysGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: '24px'
    },
    
    dayCard: {
      border: `1px solid ${theme.gray[200]}`,
      borderRadius: '12px',
      padding: '20px',
      backgroundColor: theme.gray[50]
    },
    
    dayHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      paddingBottom: '16px',
      borderBottom: `1px solid ${theme.gray[200]}`
    },
    
    dayDate: {
      fontSize: '16px',
      fontWeight: '600',
      color: theme.gray[900]
    },
    
    // Status Pills
    statusGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
      gap: '8px',
      marginBottom: '20px'
    },
    
    statusItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 12px',
      borderRadius: '6px',
      backgroundColor: 'white',
      fontSize: '14px'
    },
    
    statusDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      marginLeft: '8px'
    },
    
    // Metrics in Day Card
    metricsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px'
    },
    
    metricCard: {
      backgroundColor: 'white',
      padding: '12px',
      borderRadius: '8px',
      textAlign: 'center'
    },
    
    metricLabel: {
      fontSize: '12px',
      color: theme.gray[600],
      marginBottom: '4px'
    },
    
    metricValue: {
      fontSize: '18px',
      fontWeight: '700'
    },
    
    // Close Button
    closeButton: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      border: 'none',
      backgroundColor: theme.gray[100],
      color: theme.gray[600],
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s'
    },
    
    // Loading and Error States
    centerContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      gap: '16px'
    },
    
    errorBox: {
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '12px',
      padding: '20px',
      color: theme.danger,
      textAlign: 'center',
      maxWidth: '500px',
      margin: '0 auto'
    },
    
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: theme.gray[500]
    }
  };

  // Animations
  const animations = `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    @keyframes slideUp {
      from { 
        opacity: 0;
        transform: translateY(20px);
      }
      to { 
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  // API call
  useEffect(() => {
    fetchCampaigns();
  }, [dateRange]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `http://localhost:5000/dashbord/autoCampv3?from=${dateRange.from}&to=${dateRange.to}`
      );
      const data = await response.json();
      setCampaigns(data.campaigns || []);
      setError(null);
    } catch (err) {
      setError('حدث خطأ في تحميل البيانات. يرجى المحاولة مرة أخرى.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const calculateCampaignTotals = (campaign) => {
    let totalProfit = 0;
    let totalOrders = 0;
    let totalSuccessfulOrders = 0;
    let totalDays = campaign.orders.length;
    let totalLoss = 0;
    let profitableDays = 0;
    let lossDays = 0;

    campaign.orders.forEach(dayObj => {
      const dayData = Object.values(dayObj)[0];
      totalProfit += dayData.Profit || 0;
      totalOrders += dayData.total_orders || 0;
      totalSuccessfulOrders += dayData.orders_count || 0;
      
      if (dayData.Profit >= 0) {
        profitableDays++;
      } else {
        lossDays++;
        totalLoss += Math.abs(dayData.Profit);
      }
    });

    return { 
      totalProfit, 
      totalOrders, 
      totalSuccessfulOrders, 
      totalDays,
      totalLoss,
      profitableDays,
      lossDays
    };
  };

  // Calculate overall summary
  const calculateOverallSummary = () => {
    let totalCampaigns = campaigns.length;
    let totalOrders = 0;
    let totalProfit = 0;
    let totalBudget = 0;
    let totalLoss = 0;
    let totalRevenue = 0;
    let totalCosts = 0;

    campaigns.forEach(campaign => {
      const totals = calculateCampaignTotals(campaign);
      totalOrders += totals.totalOrders;
      totalBudget += campaign.bg || 0;
      
      // حساب الأرباح والخسائر
      if (totals.totalProfit >= 0) {
        totalProfit += totals.totalProfit;
      } else {
        totalLoss += Math.abs(totals.totalProfit);
      }
      
      // حساب الإيرادات والتكاليف من البيانات اليومية
      campaign.orders.forEach(dayObj => {
        const dayData = Object.values(dayObj)[0];
        if (dayData.details) {
          totalRevenue += dayData.details.totalRevenue || 0;
          totalCosts += (dayData.details.totalCost || 0) + (dayData.details.totalShipping || 0);
        }
      });
    });

    const netProfit = totalProfit - totalLoss;

    return { 
      totalCampaigns, 
      totalOrders, 
      totalProfit, 
      totalBudget, 
      totalLoss, 
      netProfit,
      totalRevenue,
      totalCosts
    };
  };

  // Status configurations
  const statusConfig = {
    miss_order: { label: 'طلب مفقود', color: theme.gray[500] },
    un_confirmed: { label: 'غير مؤكد', color: theme.warning },
    cancelled: { label: 'ملغي', color: theme.danger },
    waiting: { label: 'انتظار', color: '#3b82f6' },
    in_progress: { label: 'قيد التنفيذ', color: '#8b5cf6' },
    done: { label: 'مكتمل', color: theme.success },
    rejected: { label: 'مرفوض', color: '#ef4444' }
  };

  // Icons
  const RefreshIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
    </svg>
  );

  const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );

  const LoadingSpinner = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={theme.primary} strokeWidth="2">
      <path d="M21 12a9 9 0 11-6.219-8.56" style={{animation: 'spin 1s linear infinite'}} />
    </svg>
  );

  // Components
  const SummaryCard = ({ icon, label, value, color }) => (
    <div style={styles.summaryCard}>
      <div style={{...styles.summaryIcon, backgroundColor: `${color}15`}}>
        <span style={{fontSize: '24px', color}}>{icon}</span>
      </div>
      <div style={styles.summaryContent}>
        <div style={styles.summaryLabel}>{label}</div>
        <div style={styles.summaryValue}>{value}</div>
      </div>
    </div>
  );

  const CampaignCard = ({ campaign }) => {
    const totals = calculateCampaignTotals(campaign);
    const profitColor = totals.totalProfit >= 0 ? theme.success : theme.danger;
    const successRate = totals.totalOrders > 0 
      ? Math.round((totals.totalSuccessfulOrders / totals.totalOrders) * 100) 
      : 0;

    return (
      <div 
        style={styles.campaignCard}
        onClick={() => setSelectedCampaign(campaign)}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
        }}
      >
        <div style={styles.campaignHeader}>
          <h3 style={styles.campaignTitle}>{campaign.name}</h3>
          <div style={styles.campaignMeta}>
            <span style={styles.campaignId}>رقم الحملة: {campaign.id}</span>
            <span style={styles.campaignBudget}>الميزانية: {campaign.bg} دينار</span>
          </div>
        </div>
        
        <div style={styles.campaignBody}>
          <div style={styles.statsRow}>
            <div style={styles.statItem}>
              <div style={{...styles.statValue, color: theme.primary}}>
                {totals.totalOrders}
              </div>
              <div style={styles.statLabel}>إجمالي الطلبات</div>
            </div>
            <div style={styles.statItem}>
              <div style={{...styles.statValue, color: theme.success}}>
                {successRate}%
              </div>
              <div style={styles.statLabel}>نسبة النجاح</div>
            </div>
            <div style={styles.statItem}>
              <div style={{...styles.statValue, color: '#8b5cf6'}}>
                {totals.totalDays}
              </div>
              <div style={styles.statLabel}>عدد الأيام</div>
            </div>
          </div>
          
          {/* صف جديد للأرباح والخسائر */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px',
            marginBottom: '12px'
          }}>
            <div style={{
              ...styles.statItem,
              backgroundColor: '#f0fdf4',
              border: `1px solid ${theme.success}20`,
              padding: '8px'
            }}>
              <div style={{...styles.statValue, color: theme.success, fontSize: '18px'}}>
                {totals.profitableDays}
              </div>
              <div style={{...styles.statLabel, fontSize: '10px'}}>أيام رابحة</div>
            </div>
            <div style={{
              ...styles.statItem,
              backgroundColor: '#fef2f2',
              border: `1px solid ${theme.danger}20`,
              padding: '8px'
            }}>
              <div style={{...styles.statValue, color: theme.danger, fontSize: '18px'}}>
                {totals.lossDays}
              </div>
              <div style={{...styles.statLabel, fontSize: '10px'}}>أيام خاسرة</div>
            </div>
          </div>
          
          <div style={{
            ...styles.profitSection,
            backgroundColor: profitColor === theme.success ? '#f0fdf4' : '#fef2f2'
          }}>
            <div style={styles.profitLabel}>صافي الربح</div>
            <div style={{...styles.profitValue, color: profitColor}}>
              {totals.totalProfit >= 0 ? '+' : ''}{totals.totalProfit.toFixed(2)} دينار
            </div>
            {totals.totalLoss > 0 && (
              <div style={{
                fontSize: '12px',
                color: theme.danger,
                marginTop: '6px',
                padding: '6px',
                backgroundColor: 'white',
                borderRadius: '4px'
              }}>
                إجمالي الخسائر: {totals.totalLoss.toFixed(2)} دينار
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const DayDetails = ({ date, data }) => {
    const profitColor = data.Profit >= 0 ? theme.success : theme.danger;
    const successRate = data.total_orders > 0 
      ? ((data.orders_count / data.total_orders) * 100).toFixed(1) 
      : 0;

    return (
      <div style={styles.dayCard}>
        <div style={styles.dayHeader}>
          <div style={styles.dayDate}>📅 {date}</div>
          <div style={{
            padding: '4px 12px',
            borderRadius: '20px',
            backgroundColor: data.Profit >= 0 ? '#f0fdf4' : '#fef2f2',
            color: profitColor,
            fontWeight: '600',
            fontSize: '14px'
          }}>
            {data.Profit >= 0 ? '+' : ''}{data.Profit.toFixed(2)} دينار
          </div>
        </div>
        
        <div style={{marginBottom: '20px'}}>
          <div style={{fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: theme.gray[700]}}>
            حالات الطلبات ({data.total_orders} طلب)
          </div>
          <div style={styles.statusGrid}>
            {Object.entries(data.status).map(([status, count]) => {
              if (count === 0) return null;
              const config = statusConfig[status];
              return (
                <div key={status} style={styles.statusItem}>
                  <span>{config.label}</span>
                  <div style={{display: 'flex', alignItems: 'center'}}>
                    <span style={{fontWeight: '600', marginLeft: '8px'}}>{count}</span>
                    <div style={{...styles.statusDot, backgroundColor: config.color}} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        <div style={styles.metricsGrid}>
          <div style={styles.metricCard}>
            <div style={styles.metricLabel}>تكلفة الإعلان/طلب</div>
            <div style={{...styles.metricValue, color: theme.primary}}>
              {Math.abs(data.ad_cost).toFixed(2)}
            </div>
          </div>
          <div style={styles.metricCard}>
            <div style={styles.metricLabel}>نسبة النجاح</div>
            <div style={{...styles.metricValue, color: theme.success}}>
              {successRate}%
            </div>
          </div>
        </div>
        
        {data.details && (
          <div style={{
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: `1px solid ${theme.gray[200]}`,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '8px',
            fontSize: '13px'
          }}>
            <div style={{textAlign: 'center'}}>
              <div style={{color: theme.gray[600]}}>الإيرادات</div>
              <div style={{fontWeight: '600', color: theme.gray[900]}}>{data.details.totalRevenue}</div>
            </div>
            <div style={{textAlign: 'center'}}>
              <div style={{color: theme.gray[600]}}>التكاليف</div>
              <div style={{fontWeight: '600', color: theme.gray[900]}}>{data.details.totalCost}</div>
            </div>
            <div style={{textAlign: 'center'}}>
              <div style={{color: theme.gray[600]}}>الشحن</div>
              <div style={{fontWeight: '600', color: theme.gray[900]}}>{data.details.totalShipping}</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const summary = calculateOverallSummary();

  return (
    <>
      <style>{animations}</style>
      <div style={styles.pageContainer}>
        {/* Header */}
        <header style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.headerTop}>
              <h1 style={styles.title}>لوحة تحكم الحملات الإعلانية</h1>
            </div>
            
            {/* Summary Cards */}
            {!loading && campaigns.length > 0 && (
              <div style={styles.summaryGrid}>
                <SummaryCard 
                  icon="📊"
                  label="عدد الحملات"
                  value={summary.totalCampaigns}
                  color={theme.primary}
                />
                <SummaryCard 
                  icon="📦"
                  label="إجمالي الطلبات"
                  value={summary.totalOrders}
                  color="#8b5cf6"
                />
                <SummaryCard 
                  icon="💰"
                  label="إجمالي الميزانية"
                  value={`${summary.totalBudget} دينار`}
                  color={theme.warning}
                />
                <SummaryCard 
                  icon="📈"
                  label="إجمالي الأرباح"
                  value={`${summary.totalProfit.toFixed(2)} دينار`}
                  color={theme.success}
                />
                <SummaryCard 
                  icon="📉"
                  label="إجمالي الخسائر"
                  value={`${summary.totalLoss.toFixed(2)} دينار`}
                  color={theme.danger}
                />
                <SummaryCard 
                  icon="💵"
                  label="صافي الربح/الخسارة"
                  value={`${summary.netProfit >= 0 ? '+' : ''}${summary.netProfit.toFixed(2)} دينار`}
                  color={summary.netProfit >= 0 ? theme.success : theme.danger}
                />
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main style={{maxWidth: '1400px', margin: '0 auto', padding: '0 24px'}}>
          {/* Date Controls */}
          <div style={styles.dateSection}>
            <div style={styles.dateControls}>
              <label style={{fontSize: '14px', fontWeight: '500', color: theme.gray[700]}}>
                الفترة الزمنية:
              </label>
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange({...dateRange, from: e.target.value})}
                style={styles.dateInput}
                onFocus={(e) => e.target.style.borderColor = theme.primary}
                onBlur={(e) => e.target.style.borderColor = theme.gray[300]}
              />
              <span style={{color: theme.gray[500]}}>إلى</span>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange({...dateRange, to: e.target.value})}
                style={styles.dateInput}
                onFocus={(e) => e.target.style.borderColor = theme.primary}
                onBlur={(e) => e.target.style.borderColor = theme.gray[300]}
              />
              <button
                onClick={fetchCampaigns}
                style={styles.updateButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#1d4ed8';
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.primary;
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <RefreshIcon />
                تحديث البيانات
              </button>
            </div>
          </div>

          {/* Content States */}
          {loading ? (
            <div style={styles.centerContent}>
              <LoadingSpinner />
              <p style={{fontSize: '18px', color: theme.gray[600]}}>جاري تحميل البيانات...</p>
            </div>
          ) : error ? (
            <div style={styles.errorBox}>
              <p style={{fontSize: '18px', marginBottom: '8px'}}>⚠️ حدث خطأ</p>
              <p>{error}</p>
            </div>
          ) : campaigns.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={{fontSize: '64px', marginBottom: '16px'}}>📭</p>
              <p style={{fontSize: '20px', fontWeight: '500', color: theme.gray[700]}}>
                لا توجد حملات في هذه الفترة
              </p>
              <p style={{fontSize: '16px', marginTop: '8px'}}>
                جرب تغيير التواريخ للبحث عن حملات أخرى
              </p>
            </div>
          ) : (
            <div style={styles.campaignGrid}>
              {campaigns.map(campaign => (
                <CampaignCard key={campaign.id} campaign={campaign} />
              ))}
            </div>
          )}
        </main>

        {/* Modal */}
        {selectedCampaign && (
          <div style={styles.modal} onClick={() => setSelectedCampaign(null)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <div>
                  <h2 style={styles.modalTitle}>{selectedCampaign.name}</h2>
                  <p style={{color: theme.gray[600], marginTop: '4px'}}>
                    تفاصيل الأداء اليومي للحملة
                  </p>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                  {(() => {
                    const totals = calculateCampaignTotals(selectedCampaign);
                    return (
                      <div style={{
                        display: 'flex',
                        gap: '16px',
                        padding: '8px 16px',
                        backgroundColor: theme.gray[100],
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}>
                        <span style={{color: theme.success}}>
                          الأرباح: {totals.totalProfit >= 0 ? totals.totalProfit.toFixed(2) : '0.00'} دينار
                        </span>
                        <span style={{color: theme.gray[400]}}>|</span>
                        <span style={{color: theme.danger}}>
                          الخسائر: {totals.totalLoss.toFixed(2)} دينار
                        </span>
                        <span style={{color: theme.gray[400]}}>|</span>
                        <span style={{
                          color: totals.totalProfit >= 0 ? theme.success : theme.danger,
                          fontWeight: '600'
                        }}>
                          الصافي: {totals.totalProfit >= 0 ? '+' : ''}{totals.totalProfit.toFixed(2)} دينار
                        </span>
                      </div>
                    );
                  })()}
                  <button
                    style={styles.closeButton}
                    onClick={() => setSelectedCampaign(null)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.gray[200];
                      e.currentTarget.style.color = theme.gray[800];
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = theme.gray[100];
                      e.currentTarget.style.color = theme.gray[600];
                    }}
                  >
                    <CloseIcon />
                  </button>
                </div>
              </div>
              
              <div style={styles.modalBody}>
                <div style={styles.daysGrid}>
                  {selectedCampaign.orders.map((dayObj, index) => {
                    const [date, data] = Object.entries(dayObj)[0];
                    return <DayDetails key={index} date={date} data={data} />;
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CampaignDashboard;